import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import '../../styles/sitePages.css';
import './AppDetailPage.css';
import { appStoreUrl, playStoreUrl, trackStoreClick } from '../../utils/storeLinks';

function PalettePathAppPage() {
  const reviews = [
    {
      author: 'Color Lover',
      rating: '★★★★★',
      text: "Beautiful and challenging! The colors are vibrant and the puzzles are perfectly designed. Can't stop playing.",
      source: 'App Store'
    },
    {
      author: 'Puzzle Gamer',
      rating: '★★★★★',
      text: "Simple concept, deep gameplay. Each puzzle feels like a mini achievement. Great for short breaks.",
      source: 'Google Play'
    },
    {
      author: 'Brain Teaser',
      rating: '★★★★☆',
      text: "Love how it makes you think spatially. The difficulty ramp is just right - challenging but never frustrating.",
      source: 'App Store'
    }
  ];

  const features = [
    { title: 'Colorful Design', description: 'Vibrant, beautiful puzzles that are a joy to look at and solve.' },
    { title: 'Connect & Fill', description: 'Connect matching colors while filling the entire board - no empty spaces allowed.' },
    { title: 'Hundreds of Levels', description: 'Carefully crafted puzzles across multiple difficulty settings.' },
    { title: 'Logical Deduction', description: 'Every puzzle has a unique solution reachable through pure logic.' },
    { title: 'Satisfying Completion', description: 'Watch the board fill with beautiful patterns as you solve.' },
    { title: 'No Time Pressure', description: 'Take your time and enjoy the puzzle-solving process.' }
  ];

  return (
    <div className="app-detail-page">
      <Helmet>
        <title>Color Flood: Logic Puzzle - Mobile App for iOS & Android | Logic Puzzles</title>
        <meta name="description" content="Download Color Flood: Logic Puzzle for iOS and Android. A colorful puzzle game where you connect matching colors while filling the board." />
        <link rel="canonical" href="https://logicpuzzles.ca/apps/palettepath" />
        <meta property="og:title" content="Color Flood: Logic Puzzle - Mobile App for iOS & Android" />
        <meta property="og:description" content="Download Color Flood. Connect matching colors while filling the board." />
        <meta property="og:url" content="https://logicpuzzles.ca/apps/palettepath" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://logicpuzzles.ca/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Color Flood: Logic Puzzle - Mobile App for iOS & Android" />
        <meta name="twitter:description" content="Download Color Flood. Connect matching colors while filling the board." />
        <meta name="twitter:image" content="https://logicpuzzles.ca/og-image.png" />
      </Helmet>

      <NavBar />

      <section className="app-detail-hero">
        <div className="container app-detail-hero-content">
          <div className="app-detail-hero-copy">
            <span className="eyebrow">Mobile app · iOS &amp; Android</span>
            <h1>Color Flood: Logic Puzzle</h1>
            <p className="app-tagline">Connect colors, fill the board</p>

            <div className="app-detail-stats">
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">Free</span>
                <span className="app-detail-stat-label">To play</span>
              </div>
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">6K+</span>
                <span className="app-detail-stat-label">Downloads</span>
              </div>
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">500+</span>
                <span className="app-detail-stat-label">Levels</span>
              </div>
            </div>

            <div className="app-detail-buttons">
              <a href={appStoreUrl('palettepath', 'apps-page', '/apps/palettepath')} target="_blank" rel="noopener" onClick={() => trackStoreClick({ app: 'palettepath', store: 'app_store', placement: 'apps-page', page: '/apps/palettepath' })}>
                <img src="/app-store-badge.svg" alt="Download on the App Store" />
              </a>
              <a href={playStoreUrl('palettepath', 'apps-page', '/apps/palettepath')} target="_blank" rel="noopener" onClick={() => trackStoreClick({ app: 'palettepath', store: 'google_play', placement: 'apps-page', page: '/apps/palettepath' })}>
                <img src="/play-store-badge.png" alt="Get it on Google Play" />
              </a>
            </div>
          </div>
          <div className="app-detail-hero-art">
            <img
              src="/assets/logos/palettepath_logo.png"
              alt="Color Flood: Logic Puzzle App Icon"
              className="app-detail-icon"
              width="200"
              height="200"
            />
          </div>
        </div>
      </section>

      <div className="app-detail-content">
        <section className="app-detail-section">
          <h2>About Color Flood: Logic Puzzle</h2>
          <p className="app-detail-description">
            Color Flood: Logic Puzzle is a colorful puzzle game where you connect matching colors while filling the entire board. 
            Draw paths between color pairs without crossing lines, and make sure every cell is covered. 
            Create beautiful patterns while exercising your logical thinking!
          </p>
        </section>

        <section className="app-detail-section">
          <h2>Features</h2>
          <div className="app-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="app-feature-card">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="app-detail-section">
          <h2>How to Play Color Flood</h2>
          <p className="app-detail-description">
            The grid contains pairs of colored dots. Connect each pair by drawing a path between them. Paths
            travel horizontally and vertically and cannot cross each other. The key constraint: every cell on
            the board must be covered by exactly one path. This means you need to figure out not just which
            cells connect each pair, but how to route all paths so they fill the entire grid with no gaps.
          </p>
          <h3>Tips for New Players</h3>
          <ul className="app-detail-list">
            <li>Start with color pairs that are close together or boxed into a corner - their paths are more constrained.</li>
            <li>If a cell has only one possible color that can reach it, that cell's assignment is forced.</li>
            <li>Paths cannot cross, so a long path can divide the board into sections that limit other paths.</li>
            <li>Think about the "fill" requirement - every cell must be used, so avoid leaving isolated pockets.</li>
          </ul>
        </section>

        <section className="app-detail-section app-reviews-section">
          <h2>What Players Are Saying</h2>
          <div className="app-reviews-grid">
            {reviews.map((review, index) => (
              <div key={index} className="app-review-card">
                <div className="app-review-header">
                  <span className="app-review-author">{review.author}</span>
                  <span className="app-review-rating">{review.rating}</span>
                </div>
                <p className="app-review-text">"{review.text}"</p>
                <span className="app-review-source">{review.source}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="app-detail-section app-cta-section sp-cta">
          <div>
            <h2>Ready to Play?</h2>
            <p>Download Color Flood: Logic Puzzle and start connecting colors!</p>
          </div>
          <div className="app-cta-buttons sp-cta__actions">
            <Link to="/play/palettepath" className="button spark">
              Play Free Online
            </Link>
            <Link to="/apps" className="button sp-on-dark">
              ← Back to All Apps
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default PalettePathAppPage;
